import { z } from 'zod';

import { baseEntitySchema } from './baseContract';
import { Currency, Locale, UpdateFrequency } from '../enums';

export const tierConfigSchema = z.object({
  pdfReaderEnabled: z.boolean().optional(),
  voiceBot: z.boolean().optional(),
  allowSharedData: z.boolean().default(false).optional(),
  disableAiInboxUnread: z.boolean().optional(),
  disableAllInboxUnread: z.boolean().optional(),
});

// Working Hours Schema from main
export const workingHoursDaySchema = z.union([
  z.boolean(), // Closed
  z.array(
    z.object({
      from: z.string().datetime(),
      to: z.string().datetime(),
    }),
  ),
]);

export const workingHoursSchema = z
  .object({
    '0': workingHoursDaySchema,
    '1': workingHoursDaySchema,
    '2': workingHoursDaySchema,
    '3': workingHoursDaySchema,
    '4': workingHoursDaySchema,
    '5': workingHoursDaySchema,
    '6': workingHoursDaySchema,
  })
  .partial()
  .nullish();

// Define organization schema - Combining fields from both branches
export const organizationSchema = baseEntitySchema.extend({
  id: z.string().uuid(),
  name: z.string().max(150),
  url: z.string().url().max(150).or(z.literal('')).nullish(),
  monthlyUsageLimit: z.number().min(0).max(999_999_999.99).optional(),
  tierConfig: tierConfigSchema.optional(),
  stripeCustomerId: z.string().nullish(),
  stripeSubscriptionId: z.string().nullish(),
  margin: z.number().min(1).max(99999.99).nullish(),
  currentMonthlyUsage: z.number().min(0).max(999_999_999.99).nullish(),
  balance: z.number().min(0).max(999_999_999.99).nullish(),
  workingHours: workingHoursSchema,
  collectionName: z.string().optional(),
  expiresAt: z.date().nullish(),
  currency: z.nativeEnum(Currency).optional(),
  locale: z.nativeEnum(Locale).optional(),
  isPartnerOrganization: z.boolean().optional(),
  users: z
    .array(
      z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        role: z.string(),
      }),
    )
    .optional(),
});

// Base organization schema has to be defined here because of cyclic imports
export const baseOrganizationEntitySchema = baseEntitySchema.extend({
  organization: organizationSchema,
});

// Create DTO schema
export const createOrganizationSchema = organizationSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    users: true,
  })
  .strict();

// Update DTO schema - Ensuring deepPartial() is used
export const updateOrganizationSchema = organizationSchema
  .deepPartial()
  .required({
    id: true,
  })
  .omit({
    users: true,
  })
  .strict();

// Export types derived from schemas
export type OrganizationDTO = z.infer<typeof organizationSchema>;
export type CreateOrganizationDTO = z.infer<typeof createOrganizationSchema>;
export type UpdateOrganizationDTO = z.infer<typeof updateOrganizationSchema>;
export type OrganizationTierConfig = z.infer<typeof tierConfigSchema>;
export type OrganizationWorkingHours = z.infer<typeof workingHoursSchema>;
export type OrganizationWorkingHoursDay = z.infer<typeof workingHoursDaySchema>;

export const updateOrganizationAutoUpdateSchema = z.object({
  documentUpdateFrequency: z.nativeEnum(UpdateFrequency),
  documentAutoUpdateEnabled: z.boolean(),
  documentChangeThreshold: z.number().min(0).max(1),
});

export type UpdateOrganizationAutoUpdateDTO = z.infer<
  typeof updateOrganizationAutoUpdateSchema
>;

/** Canonical active-organization UI context, independent of memberships. */
export const currentOrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  url: z.string().nullish(),
  currency: z.nativeEnum(Currency),
  monthlyUsageLimit: z.number(),
  currentMonthlyUsage: z.number(),
  balance: z.number(),
  tierConfig: tierConfigSchema,
  capabilities: z.object({ voice: z.boolean() }),
});

export type CurrentOrganizationDTO = z.infer<typeof currentOrganizationSchema>;
